import { api } from '@/lib/axios';
import type { ApiResponse, PaginatedResponse } from '@/types/api';

import type {
  Drawing,
  DrawingCreated,
  DrawingPage,
  Pin,
  Project,
  ProjectCreateInput,
  UploadTarget,
} from './types';

export interface ProjectStats {
  project_id: string;
  observation_count: number;
  resolved_count: number;
  critical_count: number;
  /** Whole percent of this project's observations that are resolved or closed. */
  resolved_pct: number;
  open_query_count: number;
}

export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (params: { limit: number; offset: number }) =>
    [...projectKeys.lists(), params] as const,
  stats: () => [...projectKeys.all, 'stats'] as const,
  detail: (id: string) => [...projectKeys.all, 'detail', id] as const,
  drawings: (projectId: string) => [...projectKeys.all, projectId, 'drawings'] as const,
  drawing: (drawingId: string) => ['drawings', drawingId] as const,
  pages: (drawingId: string) => ['drawings', drawingId, 'pages'] as const,
  pins: (drawingId: string, page: number) => ['drawings', drawingId, 'pins', page] as const,
};

// ── Projects ──────────────────────────────────────────────────────────────
export async function listProjects(params: {
  limit: number;
  offset: number;
}): Promise<PaginatedResponse<Project>> {
  const res = await api.get<PaginatedResponse<Project>>('/projects', { params });
  return res.data;
}

export async function getProject(id: string): Promise<Project> {
  const res = await api.get<ApiResponse<Project>>(`/projects/${id}`);
  return res.data.data;
}

export async function fetchProjectStats(): Promise<ProjectStats[]> {
  const res = await api.get<ProjectStats[]>('/projects/stats');
  return res.data;
}

export interface ProjectMember {
  id: string;
  email: string;
  full_name: string;
  role: string;
  avatar_url: string | null;
  is_active: boolean;
}

export async function listProjectMembers(projectId: string): Promise<ProjectMember[]> {
  const res = await api.get<ProjectMember[]>(`/projects/${projectId}/members`);
  return res.data;
}

export async function addProjectMember(projectId: string, userId: string): Promise<void> {
  await api.post(`/projects/${projectId}/members`, { user_id: userId });
}

export async function removeProjectMember(projectId: string, userId: string): Promise<void> {
  await api.delete(`/projects/${projectId}/members/${userId}`);
}

/** Ids of users currently assigned to a project (for the edit modal). */
export async function fetchProjectMemberIds(projectId: string): Promise<string[]> {
  const members = await listProjectMembers(projectId);
  return members.map((m) => m.id);
}

/**
 * Replace the project's whole member set.
 *
 * Diffs against the current members and applies the delta with the always-live
 * POST/DELETE member endpoints, so it works without the newer bulk PUT route.
 */
export async function setProjectMembers(projectId: string, userIds: string[]): Promise<void> {
  const current = await listProjectMembers(projectId);
  const currentIds = new Set(current.map((m) => m.id));
  const want = new Set(userIds);
  const toAdd = userIds.filter((id) => !currentIds.has(id));
  const toRemove = [...currentIds].filter((id) => !want.has(id));
  await Promise.all([
    ...toAdd.map((id) => addProjectMember(projectId, id)),
    ...toRemove.map((id) => removeProjectMember(projectId, id)),
  ]);
}

export async function createProject(input: ProjectCreateInput): Promise<Project> {
  const res = await api.post<ApiResponse<Project>>('/projects', input);
  return res.data.data;
}

export interface ProjectUpdateInput {
  name?: string;
  category?: string;
  description?: string;
  client_name?: string;
  address?: string;
  status?: string;
  current_stage?: string;
  stage_label?: string;
  instructions?: string;
}

export async function updateProject(id: string, input: ProjectUpdateInput): Promise<Project> {
  const res = await api.patch<ApiResponse<Project>>(`/projects/${id}`, input);
  return res.data.data;
}

export async function deleteProject(id: string): Promise<void> {
  await api.delete(`/projects/${id}`);
}

// ── Drawings ──────────────────────────────────────────────────────────────
export async function listDrawings(projectId: string): Promise<Drawing[]> {
  const res = await api.get<PaginatedResponse<Drawing>>(`/projects/${projectId}/drawings`, {
    params: { limit: 100, offset: 0 },
  });
  return res.data.data;
}

export async function getDrawing(id: string): Promise<{ drawing: Drawing; url: string }> {
  const res = await api.get<ApiResponse<{ drawing: Drawing; url: string }>>(`/drawings/${id}`);
  return res.data.data;
}

export async function initDrawingUpload(
  projectId: string,
  body: {
    title: string;
    filename: string;
    content_type: string;
    stage: string;
    discipline: string;
    drawing_number: string;
  },
): Promise<DrawingCreated> {
  const res = await api.post<ApiResponse<DrawingCreated>>(
    `/projects/${projectId}/drawings`,
    body,
  );
  return res.data.data;
}

/** PUT the file bytes to the backend blob endpoint (which handles S3 server-side). */
export async function uploadBytes(
  target: UploadTarget,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  const progressConfig = onProgress
    ? {
        onUploadProgress: (e: { loaded: number; total?: number }) => {
          if (e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      }
    : {};

  // All uploads go through our backend blob endpoint (no direct S3 needed).
  await api.put(target.url.replace('/api/v1', ''), file, {
    headers: { 'Content-Type': target.headers['Content-Type'] ?? file.type },
    ...progressConfig,
  });
}

export async function completeDrawing(
  drawingId: string,
  sizeBytes: number,
): Promise<{ drawing: Drawing; url: string }> {
  const res = await api.post<ApiResponse<{ drawing: Drawing; url: string }>>(
    `/drawings/${drawingId}/complete`,
    { size_bytes: sizeBytes },
  );
  return res.data.data;
}

export async function deleteDrawing(drawingId: string): Promise<void> {
  await api.delete(`/drawings/${drawingId}`);
}

export async function listPages(drawingId: string): Promise<DrawingPage[]> {
  const res = await api.get<ApiResponse<DrawingPage[]>>(`/drawings/${drawingId}/pages`);
  return res.data.data;
}

// ── Pins ──────────────────────────────────────────────────────────────────
export async function listPins(drawingId: string, page?: number): Promise<Pin[]> {
  const res = await api.get<ApiResponse<Pin[]>>(`/drawings/${drawingId}/pins`, {
    params: page ? { page } : undefined,
  });
  return res.data.data;
}

export async function createPin(
  drawingId: string,
  body: { page: number; x: number; y: number; label?: string; note?: string; color?: string },
): Promise<Pin> {
  const res = await api.post<ApiResponse<Pin>>(`/drawings/${drawingId}/pins`, body);
  return res.data.data;
}

export async function updatePin(
  pinId: string,
  body: Partial<{ x: number; y: number; label: string; note: string; status: string }>,
): Promise<Pin> {
  const res = await api.patch<ApiResponse<Pin>>(`/pins/${pinId}`, body);
  return res.data.data;
}

export async function deletePin(pinId: string): Promise<void> {
  await api.delete(`/pins/${pinId}`);
}
